/**
 * Převod hodnot mezi měsíci a roky (DEV-2165).
 *
 * Doba trvání, výpovědní lhůta, autoprolongace a lhůty pro výpověď se v aplikaci zadávají
 * buď v měsících, nebo v letech — podle přepínače `*MonthsUnit` ('M' / 'Y'). Samotná hodnota
 * se ale ukládá v jednom poli, takže se pořád někde převádí.
 *
 * Před tímhle modulem se ten převod psal inline na **49 místech** — v effectech, reducerech,
 * selektorech, komponentách i službách, každé jako vlastní ternár. Kolem toho vznikly záplaty
 * kompenzující, kterým z těch míst hodnota zrovna prošla (viz DEV-2153, kde se v jedné
 * komponentě dělilo dvakrát a drželo to jen shodou dvou nesouvisejících podmínek).
 *
 * ## Proč funkce a ne služba
 *
 * 15 z těch 49 míst je v **selektorech a reducerech**, kam se Angular DI nedostane. Musí to
 * tedy být obyčejná exportovaná funkce. Vedlejší přínos: testuje se bez `TestBed` a jde
 * použít i uvnitř `createSelector`.
 *
 * ## Chování u prázdných hodnot
 *
 * Nullish hodnota projde nezměněná (`null` → `null`, `undefined` → `undefined`). Původní
 * inline ternáry v tomhle případě vyráběly `NaN` (`undefined / 12`), což se pak maskovalo
 * až u konzumenta přes `|| 0`. `ConvertDateUnitService` naopak nullish tvrdě přepisoval na `0`.
 * Ani jedno není dobré: `NaN` teče dál a `0` je platná hodnota, kterou nelze odlišit od
 * „nezadáno". Proto se tady nullish zachovává.
 *
 * Číselné stringy se parsují (formuláře posílají hodnoty z inputů jako text). Neparsovatelný
 * vstup dá `null`, ne `NaN`.
 */

export type MonthYearUnit = 'M' | 'Y'

export const MONTHS_PER_YEAR = 12

const YEARS: MonthYearUnit = 'Y'

/**
 * Bezpečné načtení čísla. Vrací `null` místo `NaN`, aby se nesmysl nešířil dál.
 */
const parseAmount = (value: unknown): number => {
    if (value === null || value === undefined || value === '') {
        return null
    }

    const parsed = typeof value === 'number' ? value : parseFloat(String(value))

    return isNaN(parsed) ? null : parsed
}

/**
 * Přepočte hodnotu zadanou v dané jednotce na **měsíce** — kanonickou jednotku pro store
 * a pro databázi.
 *
 * @example toMonths(5, 'Y') === 60
 * @example toMonths(18, 'M') === 18
 */
export const toMonths = (value: unknown, unit: string): number => {
    const amount = parseAmount(value)

    if (amount === null) {
        return value === undefined ? undefined : null
    }

    return unit === YEARS ? amount * MONTHS_PER_YEAR : amount
}

/**
 * Přepočte měsíce na jednotku, ve které se hodnota uživateli **zobrazuje**.
 *
 * @example toDisplayUnits(60, 'Y') === 5
 * @example toDisplayUnits(18, 'M') === 18
 */
export const toDisplayUnits = (months: unknown, unit: string): number => {
    const amount = parseAmount(months)

    if (amount === null) {
        return months === undefined ? undefined : null
    }

    return unit === YEARS ? amount / MONTHS_PER_YEAR : amount
}
